import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
    AccountMovementDetailDTO,
    AccountMovementDetailItemDTO,
    AccountMovementFiscalFormDTO,
    ArcaReceiptState,
} from '../../dto/CurrentAccountDTO';
import { useCurrentAccountService } from '../../servicesHooks/CurrentAccount/useCurrentAccountService';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';
import { formatMoney } from '../../utils/money';
import { formatDateOnly } from '../../utils/date';
import { formatSignedMoney } from './currentAccountFormat';

interface MovementDetailDialogProps {
    accountMovementId: number | null;
    onHide: () => void;
}

const ARCA_STATUS: Record<ArcaReceiptState, { label: string; className: string; icon: string }> = {
    [ArcaReceiptState.APPROVED]: { label: 'Aprobado', className: 'nv-status-ok', icon: 'pi pi-check-circle' },
    [ArcaReceiptState.OBSERVED]: { label: 'Observado', className: 'nv-status-warn', icon: 'pi pi-exclamation-triangle' },
    [ArcaReceiptState.REJECTED]: { label: 'Rechazado', className: 'nv-status-err', icon: 'pi pi-times-circle' },
};

function ArcaStatus({ state }: { state: ArcaReceiptState | null }) {
    if (state == null) return <span className="nv-muted">Sin autorizar</span>;
    const status = ARCA_STATUS[state];
    return (
        <span className={status.className}>
            <i className={status.icon} aria-hidden="true" />
            {status.label}
        </span>
    );
}

function ReadOnly({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="nv-ro">
            <dt className="nv-ro-label">{label}</dt>
            <dd className="nv-ro-value">{children}</dd>
        </div>
    );
}

function formatQuantity(quantity: number | null): string {
    return quantity == null ? '—' : quantity.toLocaleString('es-AR', { maximumFractionDigits: 2 });
}

/** Valor que el usuario va a querer buscar en otro lado (número, CAE): con un botón para copiarlo. */
function CopyableValue({ value, what, onCopy }: { value: string | null; what: string; onCopy: (value: string, what: string) => void }) {
    if (!value) return <>—</>;
    return (
        <span className="nv-row-8">
            <span className="nv-strong">{value}</span>
            <Button
                type="button"
                text
                className="nv-icon"
                icon="pi pi-copy"
                aria-label={`Copiar ${what}`}
                tooltip={`Copiar ${what}`}
                tooltipOptions={{ event: 'both', position: 'top', className: 'nv-tooltip' }}
                onClick={() => onCopy(value, what)}
            />
        </span>
    );
}

function FiscalFormSection({ fiscal, onCopy }: { fiscal: AccountMovementFiscalFormDTO; onCopy: (value: string, what: string) => void }) {
    return (
        <section className="nv-section" aria-labelledby="nv-movement-fiscal">
            <h3 className="nv-section-title" id="nv-movement-fiscal">Comprobante</h3>
            <dl className="nv-summary-grid">
                <ReadOnly label="Número">
                    <CopyableValue value={fiscal.formnumberdisplay} what="el número" onCopy={onCopy} />
                </ReadOnly>
                <ReadOnly label="Fecha del comprobante">{formatDateOnly(fiscal.formdate)}</ReadOnly>
                <ReadOnly label="Estado">{fiscal.statedesc || '—'}</ReadOnly>
                <ReadOnly label="Emisor">{fiscal.invoicingcompanyname || '—'}</ReadOnly>
            </dl>
            {fiscal.externalnumber && (
                <dl className="nv-summary-grid">
                    <ReadOnly label="Estado en ARCA"><ArcaStatus state={fiscal.receiptstate} /></ReadOnly>
                    <ReadOnly label="CAE">
                        <CopyableValue value={fiscal.cae} what="el CAE" onCopy={onCopy} />
                    </ReadOnly>
                    <ReadOnly label="Vencimiento del CAE">{formatDateOnly(fiscal.caedue)}</ReadOnly>
                </dl>
            )}
        </section>
    );
}

export default function MovementDetailDialog({ accountMovementId, onHide }: MovementDetailDialogProps) {
    const { getMovementDetail } = useCurrentAccountService();
    const notify = useNotify();
    const [detail, setDetail] = useState<AccountMovementDetailDTO | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (accountMovementId == null) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        getMovementDetail(accountMovementId)
            .then((data: AccountMovementDetailDTO) => { if (!cancelled) setDetail(data); })
            .catch((error: unknown) => {
                parseApiError(error);
                if (cancelled) return;
                setDetail(null);
                notify.error('No se pudo cargar el detalle del movimiento. Intentá de nuevo.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [accountMovementId, notify]);

    function handleCopy(value: string, what: string) {
        navigator.clipboard.writeText(value)
            .then(() => notify.success(`Copiaste ${what}`))
            .catch(() => notify.error(`No se pudo copiar ${what}. Intentá de nuevo.`));
    }

    const header = detail?.fiscalform?.formnumberdisplay
        ? `Detalle del movimiento · ${detail.formtype ?? 'Comprobante'} ${detail.fiscalform.formnumberdisplay}`
        : 'Detalle del movimiento';

    return (
        <Dialog
            header={header}
            visible={accountMovementId != null}
            onHide={onHide}
            breakpoints={{ '960px': '100vw' }}
            className="nv-scope nv-dialog"
            modal
        >
            <div className="nv-dialog-body">
                {loading && (
                    <div className="nv-loading" role="status">
                        <ProgressSpinner strokeWidth="4" style={{ width: 32, height: 32 }} />
                        <span>Cargando detalle…</span>
                    </div>
                )}

                {!loading && detail && (
                    <>
                        <section className="nv-section" aria-labelledby="nv-movement-data">
                            <h3 className="nv-section-title" id="nv-movement-data">Movimiento</h3>
                            <dl className="nv-summary-grid">
                                <ReadOnly label="Fecha">{formatDateOnly(detail.movementdate)}</ReadOnly>
                                <ReadOnly label="Tipo">{detail.formtype || '—'}</ReadOnly>
                                <ReadOnly label="Cliente">{detail.customername || '—'}</ReadOnly>
                                <ReadOnly label="Medio de pago">{detail.paymentmethod || '—'}</ReadOnly>
                                <ReadOnly label="Importe">
                                    <span className="nv-strong">{formatSignedMoney(detail.totalamount)}</span>
                                </ReadOnly>
                            </dl>
                        </section>

                        <hr className="nv-divider" />

                        {detail.fiscalform
                            ? <FiscalFormSection fiscal={detail.fiscalform} onCopy={handleCopy} />
                            : <p className="nv-help">El movimiento no tiene un comprobante asociado.</p>}

                        <hr className="nv-divider" />

                        <section className="nv-section" aria-labelledby="nv-movement-items">
                            <h3 className="nv-section-title" id="nv-movement-items">Ítems</h3>
                            <DataTable
                                value={detail.items ?? []}
                                className="nv-load-table nv-compact"
                                scrollable
                                scrollHeight="360px"
                                emptyMessage="El comprobante no tiene ítems."
                            >
                                <Column
                                    header="Descripción"
                                    className="nv-col-item"
                                    body={(item: AccountMovementDetailItemDTO) => (
                                        <div className="nv-stack-4">
                                            <span className="nv-truncate">{item.nomenclatordescription || item.description || '—'}</span>
                                            {item.nomenclatordescription && item.description && item.nomenclatordescription !== item.description && (
                                                <span className="nv-help nv-truncate">{item.description}</span>
                                            )}
                                        </div>
                                    )}
                                />
                                <Column header="Código" className="nv-col-date" body={(item: AccountMovementDetailItemDTO) => item.nomenclatorcode || '—'} />
                                <Column header="Cantidad" align="right" alignHeader="right" className="nv-col-qty" body={(item: AccountMovementDetailItemDTO) => formatQuantity(item.quantity)} />
                                <Column header="Valor unitario" align="right" alignHeader="right" className="nv-col-money" body={(item: AccountMovementDetailItemDTO) => formatMoney(item.unitprice)} />
                                <Column header="Subtotal" align="right" alignHeader="right" className="nv-col-money" body={(item: AccountMovementDetailItemDTO) => formatMoney(item.subtotal)} />
                                <Column header="IVA" align="right" alignHeader="right" className="nv-col-money" body={(item: AccountMovementDetailItemDTO) => formatMoney(item.vatamount)} />
                                <Column header="Total" align="right" alignHeader="right" className="nv-col-money" body={(item: AccountMovementDetailItemDTO) => <span className="nv-strong">{formatMoney(item.totalamount)}</span>} />
                            </DataTable>
                        </section>
                    </>
                )}

                {!loading && !detail && (
                    <p className="nv-help">No hay datos del movimiento.</p>
                )}
            </div>
        </Dialog>
    );
}
